/* eslint-disable max-len */
import React, { useMemo } from 'react';

import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';

import { Button } from '@components/Base/Button';
import EmptyMessagePage from '@components/Base/EmptyMessagePage';
import Pagination from '@components/Base/Pagination';
import { CustomColumnDef } from '@components/Base/Table/types';
import TransactionsTable from '@components/Transactions/TransactionsTable';
import { createColumns } from '@components/Transactions/TransactionsTable/columns';
import { AppliedFilters } from '@containers/transactions';
import { Country, Transactions } from '@containers/transactions/types';
import { getPageCount } from '@utils/helpers';
import ClipBoardIcon from 'app/images/clipboard.svg';
import NoResultsFound from 'app/images/svgs/noResultsFound.svg';

import messages from './messages';
import TransactionsFilters from './TransactionFilters';

interface TransactionListProps {
  transactions: Transactions[];
  countryList: Country[];
  transactionsLoading: boolean;
  updateAppliedFilters: React.Dispatch<React.SetStateAction<AppliedFilters>>;
  appliedFilters: AppliedFilters;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  setPagination: React.Dispatch<React.SetStateAction<{ pageIndex: number; pageSize: number }>>;
  totalCount: number;
  downloadCompleteTransactionList: () => void;
  loadingCompleteTransactionList: boolean;
  triggerFilterMPEvent: (filters: AppliedFilters) => void;
}

const TransactionList = (props: TransactionListProps) => {
  const {
    transactions,
    countryList,
    transactionsLoading,
    appliedFilters,
    updateAppliedFilters,
    pagination,
    setPagination,
    totalCount,
    downloadCompleteTransactionList,
    loadingCompleteTransactionList,
    triggerFilterMPEvent,
  } = props;

  const pageCount = getPageCount(totalCount, pagination);

  const showEmptyPage = isEmpty(appliedFilters);

  const renderEmptyPage = isEmpty(transactions) && !transactionsLoading && showEmptyPage;

  const renderNoResultsFoundPage = isEmpty(transactions) && !showEmptyPage;

  const columnsIssued = useMemo(
    () => createColumns().filter((column) => column !== null) as CustomColumnDef<Transactions>[],
    [],
  );

  const countries = useMemo(
    () =>
      countryList.map((country) => ({
        label: country.description,
        value: country.description,
      })),
    [countryList],
  );

  const nextPage = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: prev.pageIndex + 1,
    }));
  };

  const prevPage = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: prev.pageIndex - 1,
    }));
  };

  const downloadTransaction = () => {
    downloadCompleteTransactionList();
  };

  const handleApplyFilters = (filters: AppliedFilters) => {
    updateAppliedFilters(filters);
    !isEmpty(filters) && triggerFilterMPEvent(filters);
  };

  return (
    <>
      {renderEmptyPage ? (
        <EmptyMessagePage icon={ClipBoardIcon} titleMessage={messages.empty_transactions} descriptionWidth="w-48" />
      ) : (
        <div className="flex w-full px-8 py-5">
          <div className="flex flex-col w-full">
            <div className="flex flex-row-reverse justify-between items-center">
              <div className="flex items-center gap-4">
                <TransactionsFilters
                  countryList={countries}
                  appliedFilters={appliedFilters}
                  handleApplyFilters={handleApplyFilters}
                />
                <div
                  className={`inline-flex items-center group cursor-pointer ${loadingCompleteTransactionList ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <ArrowDownTrayIcon className="w-5 h-5 text-blue-700 cursor-pointer mr-2 group-hover:text-blue-700" />
                  <Button
                    type="button"
                    variant="ghost"
                    data-testid="download-btn"
                    className="bg-transparent text-blue-700 font-medium text-sm p-0 m-0 
      group-hover:bg-transparent hover:bg-transparent"
                    onClick={downloadTransaction}
                    isLoading={loadingCompleteTransactionList}
                  >
                    <span className="text-sm font-medium">
                      <FormattedMessage {...messages.download_list} />
                    </span>
                  </Button>
                </div>
                <div className="w-0 h-6 border border-gray-300" />
                <Pagination
                  total={totalCount}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.pageIndex}
                  summary
                  previousPage={prevPage}
                  nextPage={nextPage}
                  getCanPreviousPage={() => pagination.pageIndex > 0}
                  getCanNextPage={() => pagination.pageIndex < pageCount - 1}
                />
              </div>
            </div>
            {renderNoResultsFoundPage ? (
              <EmptyMessagePage
                icon={NoResultsFound}
                titleMessage={messages.empty_record_title}
                descriptionMessage={messages.empty_record_description}
              />
            ) : (
              <TransactionsTable
                transactions={transactions}
                columns={columnsIssued}
                transactionsLoading={transactionsLoading}
                pagination={pagination}
                pageCount={pageCount}
                setPagination={setPagination}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionList;
